import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  dropTargetForElements,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { faChevronDown, faChevronRight, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ColumnDef,
  ColumnSort,
  ExpandedState,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  createColumnHelper,
} from '@tanstack/react-table'
import { Flex, Pagination } from 'antd'
import isNil from 'lodash.isnil'
import styled from 'styled-components'
import { twMerge } from 'tailwind-merge'
import { v4 } from 'uuid'

import DragIndicator from 'src/components/DragIndicator'
import EmptyState from 'src/components/EmptyState'
import { IconOnlyButton } from 'src/components/shared/StyledComponents'
import useDisclosure from 'src/hooks/use-disclosure'
import { useMergeRefs } from 'src/hooks/use-merge-refs'
import { KeyboardKey } from 'src/types'

import { getReorderingIndices } from '../../../utils/drag-and-drop'
import { RowLevelAction, Size } from '../../types'
import Checkbox from '../Checkbox'
import VStack from '../Flex/VStack'
import Header from '../Header'
import Tooltip from '../Tooltip/Tooltip'

import { TableContext } from './context'
import TableActionToolbar from './TableActionToolbar'
import TableHeader from './TableHeader'
import TableRow from './TableRow'
import { TableColumnId, TableDropTargetDragState, TableRowDragState } from './types'
import { useStickyScrollDetection } from './use-sticky-scroll-detection'
import { useTable } from './use-table'
import { isTableDropTargetDragState, isTableRowDragState } from './util'

const StyledTable = styled.table<{
  $isLoading: boolean
  $layout: React.CSSProperties['tableLayout']
  $minWidth?: React.CSSProperties['minWidth']
}>`
  table-layout: ${({ $layout }) => $layout};
  ${({ $minWidth }) => !isNil($minWidth) && `min-width: ${$minWidth};`};
  width: 100%;
  padding: 0;
  border-collapse: collapse;
  border-spacing: 0px;
  ${({ $isLoading }) =>
    $isLoading
      ? `
    opacity: 0.5;
    pointer-events: none;
  `
      : ''}
`
const StyledEmptyStateWrapper = styled.div<{ $isDropTarget: boolean }>`
  > div {
    padding: 12px 0px;
    transition: background-color 0.25s;
    ${({ $isDropTarget, theme }) =>
      $isDropTarget &&
      `
      background-color: ${theme['blue-1']};
    `}
  }
`

export type OnRowReorderedInput<TData> = {
  // The logical movement: move the item at sourceIndex to destinationIndex
  // This data should be sufficient for 99% of cases
  sourceIndex: number
  destinationIndex: number
  rawData: {
    // The physical movement:
    // sourceRow and destinationRow are the rows that the user happened to interact with visually
    // closestEdge is the general position of the cursor within destinationRow at the time that the sourceRow was released
    sourceRow: Row<TData>
    destinationRow: Row<TData>
    closestEdge: Edge
  }
}

type Props<TData = unknown> = {
  actions?: React.ReactNode
  columns: ColumnDef<TData, any>[]
  data: TData[] | null | undefined
  enablePagination?: boolean
  enableRowExpansion?: boolean
  enableRowSelection?: boolean
  enableRowReordering?: boolean
  enableSearching?: boolean
  enableSearchShortcut?: boolean
  externalSearchInputRef?: React.RefObject<HTMLInputElement>
  emptyStateAction?: React.ReactNode
  emptyStateMessage?: React.ReactNode
  expanded?: ExpandedState
  expandButtonTooltipContent?: {
    closed: React.ReactNode
    open: React.ReactNode
  }
  getRowCanExpand?: (rowData: TData) => boolean
  getRowExpandedContent?: (rowData: TData) => React.ReactNode
  getRowId: (rowData: TData, index: number) => string
  getRowIsSelectable?: (rowData: TData) => boolean
  getRowIsReorderable?: (rowData: TData) => boolean
  getRowIsHighlighted?: (rowData: TData) => boolean
  hasBorderRadius?: boolean
  isLoading?: boolean
  isSortedOnClient?: boolean
  layout?: React.CSSProperties['tableLayout']
  minWidth?: React.CSSProperties['minWidth']
  onExpandedChange?: (expanded: ExpandedState) => void
  onHoveredRowChange?: (row: Row<TData> | undefined) => void
  onPaginationChange?: (pagination: PaginationState) => void
  onRowDroppedIntoTable?: (sourceRow: Row<TData>) => void
  onRowReordered?: (input: OnRowReorderedInput<TData>) => void
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void
  onSearchQueryChange?: (value: string) => void
  onSortingChange?: OnChangeFn<ColumnSort[]> | undefined
  /*
   * This prop should only be used if you are dragging rows across multiple tables. Otherwise, don't use it!
   * Add a new custom getCanReorderRow prop instead if you don't want to override the same-table invariant
   * but still need to add additional restrictions on which rows can be reordered.
   *
   * You MUST define a real invariant to replace it, do not just return true. Reordering events are global,
   * so you need to identify that the row belongs to the specific group of tables you are dragging between
   * to avoid conflicting with other draggable elements on screen.
   */
  overrideReorderingWithinSameTableInvariant?: (rowDragState: TableRowDragState<TData>) => boolean
  pagination?: PaginationState
  reorderButtonTooltipContent?: React.ReactNode
  rowLevelActions?: {
    primaryActions?: RowLevelAction[]
    secondaryActions?: RowLevelAction[]
  }
  rowSelection?: RowSelectionState
  searchInputPlaceholder?: string
  searchQuery?: string
  showCount?: boolean
  shouldHideEmptyStateImage?: boolean
  shouldHideHeader?: boolean
  size?: Size
  sorting?: ColumnSort[]
  subtitle?: React.ReactNode
  title?: React.ReactNode
  totalCount?: number
  verticalAlign?: React.CSSProperties['verticalAlign']
} & (
  | {
      isPaginatedOnClient?: false
      totalCount?: number
    }
  | {
      isPaginatedOnClient: true
      totalCount?: never
    }
) &
  (
    | {
        emptyStateImage?: React.ReactNode
        emptyStateIcon?: never
      }
    | {
        emptyStateImage?: never
        emptyStateIcon?: IconDefinition
      }
  )
const TableContent = <TData,>(props: Props<TData>) => {
  const {
    actions,
    columns: propColumns,
    data,
    enablePagination = false,
    enableRowExpansion = false,
    enableRowReordering = false,
    enableRowSelection = false,
    enableSearching = false,
    enableSearchShortcut = false,
    externalSearchInputRef,
    emptyStateAction = null,
    emptyStateImage = <EmptyState.Image />,
    emptyStateIcon,
    emptyStateMessage = 'No results found',
    expanded,
    expandButtonTooltipContent,
    getRowCanExpand: propGetRowCanExpand,
    getRowExpandedContent,
    getRowId,
    getRowIsSelectable: propGetRowIsSelectable,
    getRowIsReorderable: propGetRowIsReorderable,
    getRowIsHighlighted: propGetRowIsHighlighted,
    hasBorderRadius = true,
    isLoading,
    isPaginatedOnClient = false,
    isSortedOnClient = false,
    layout = 'auto',
    minWidth,
    onExpandedChange,
    onHoveredRowChange,
    onPaginationChange,
    onRowDroppedIntoTable,
    onRowReordered,
    onRowSelectionChange,
    onSearchQueryChange,
    onSortingChange,
    overrideReorderingWithinSameTableInvariant,
    pagination,
    reorderButtonTooltipContent,
    rowLevelActions,
    rowSelection,
    searchInputPlaceholder,
    searchQuery,
    showCount = false,
    size = 'md',
    shouldHideEmptyStateImage,
    shouldHideHeader,
    sorting,
    subtitle,
    title,
    totalCount: propTotalCount,
    verticalAlign,
  } = props
  const [uuid] = useState(v4())

  const rowSelectionColumn = useMemo(() => {
    const columnHelper = createColumnHelper<TData>()
    return columnHelper.display({
      id: TableColumnId.Select,
      meta: {
        dangerouslyOverrideHorizontalPadding: layout === 'auto' ? '8px' : '4px', // TODO(GOV-303): allow customizing,
        onClick: (e: React.MouseEvent<HTMLTableCellElement>, row: Row<TData>) => {
          const toggle = row.getToggleSelectedHandler()
          toggle(e)
          e.stopPropagation()
          e.preventDefault()
        },
        shouldRenderHeaderAsTd: true,
        stickiness: 'left',
        width: layout === 'auto' ? '0px' : '22px', // In an auto layout this is taken as a suggestion, not a literal value
      },
      header: ({ table }) => {
        let value: boolean | 'indeterminate' = false
        let ariaLabel = 'Select all rows'
        if (table.getIsAllRowsSelected()) {
          value = true
          ariaLabel = 'Deselect all rows'
        } else if (table.getIsSomeRowsSelected()) {
          value = 'indeterminate'
        }
        return (
          <Checkbox
            ariaLabel={ariaLabel}
            value={value}
            onValueChange={() => table.toggleAllRowsSelected()}
          />
        )
      },
      cell: ({ row }) => (
        <Flex justify="center" style={{ width: '100%' }}>
          <Checkbox
            ariaLabel="Toggle row selected"
            value={row.getIsSelected()}
            onValueChange={row.getToggleSelectedHandler()}
            isDisabled={!row.getCanSelect()}
          />
        </Flex>
      ),
    })
  }, [layout])

  const getRowCanExpand = useCallback(
    (row: Row<TData>) => propGetRowCanExpand?.(row.original) ?? true,
    [propGetRowCanExpand]
  )
  const getRowIsSelectable = useCallback(
    (row: Row<TData>) => propGetRowIsSelectable?.(row.original) ?? true,
    [propGetRowIsSelectable]
  )
  const getRowIsReorderable = useCallback(
    (row: Row<TData>) => propGetRowIsReorderable?.(row.original) ?? true,
    [propGetRowIsReorderable]
  )
  const getRowIsHighlighted = useCallback(
    (row: Row<TData>) => propGetRowIsHighlighted?.(row.original) ?? false,
    [propGetRowIsHighlighted]
  )

  const rowReorderColumn = useMemo(() => {
    const columnHelper = createColumnHelper<TData>()
    return columnHelper.display({
      id: TableColumnId.Reorder,
      header: '',
      meta: {
        width: layout === 'auto' ? '0px' : '32px', // In an auto layout this is taken as a suggestion, not a literal value
        dangerouslyOverrideHorizontalPadding: '4px',
        stickiness: 'left',
      },
      cell: ({ row }) =>
        getRowIsReorderable(row) ? (
          <DragIndicator tooltipContent={reorderButtonTooltipContent ?? 'Drag to reorder'} />
        ) : null,
    })
  }, [getRowIsReorderable, layout, reorderButtonTooltipContent])

  const rowExpansionColumn = useMemo(() => {
    const columnHelper = createColumnHelper<TData>()
    return columnHelper.display({
      id: TableColumnId.Expand,
      header: '',
      meta: {
        width: layout === 'auto' ? '12px' : '32px', // In an auto layout this is taken as a suggestion, not a literal value
        dangerouslyOverrideHorizontalPadding: '4px',
        stickiness: 'left',
      },
      cell: ({ row }) => {
        const isExpandable = row.getCanExpand()
        if (!isExpandable) {
          return null
        }

        const isExpanded = row.getIsExpanded()
        const handleExpand = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          e.preventDefault()
          const handler = row.getToggleExpandedHandler()
          handler()
        }

        return (
          <Tooltip
            content={
              isExpanded
                ? (expandButtonTooltipContent?.open ?? 'Collapse row')
                : (expandButtonTooltipContent?.closed ?? 'Expand row')
            }
          >
            <Flex justify="center" style={{ width: '100%' }}>
              <IconOnlyButton
                icon={<FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />}
                size="small"
                onClick={handleExpand}
              />
            </Flex>
          </Tooltip>
        )
      },
    })
  }, [expandButtonTooltipContent?.closed, expandButtonTooltipContent?.open, layout])

  const hasData = !!data?.length

  const totalCount = isPaginatedOnClient ? data?.length : propTotalCount
  const isSortingEnabled = !!totalCount && !!onSortingChange // react-table does not respond to sort changes if there are no rows
  const isPaginationEnabled =
    enablePagination && !isNil(pagination) && !isNil(totalCount) && pagination.pageSize < totalCount
  const isAnyRowReorderable =
    hasData && (!propGetRowIsReorderable || data.some((d) => propGetRowIsReorderable?.(d)))
  const isRowReorderingEnabled = enableRowReordering && !!onRowReordered && isAnyRowReorderable

  const isAnyRowSelectable =
    hasData && (!propGetRowIsSelectable || data.some((d) => propGetRowIsSelectable?.(d)))
  const isRowSelectionEnabled =
    enableRowSelection && !!rowSelection && !!onRowSelectionChange && isAnyRowSelectable

  const isAnyRowExpandable =
    hasData && (!propGetRowCanExpand || data.some((d) => propGetRowCanExpand?.(d)))
  const isRowExpansionEnabled =
    enableRowExpansion &&
    !!expanded &&
    !!getRowExpandedContent &&
    !!onExpandedChange &&
    isAnyRowExpandable
  const isSearchingEnabled = enableSearching && !isNil(onSearchQueryChange)
  const columns = useMemo(
    () => [
      ...(isRowReorderingEnabled ? [rowReorderColumn] : []),
      ...(isRowSelectionEnabled ? [rowSelectionColumn] : []),
      ...(isRowExpansionEnabled ? [rowExpansionColumn] : []),
      ...propColumns,
    ],
    [
      propColumns,
      isRowExpansionEnabled,
      isRowReorderingEnabled,
      isRowSelectionEnabled,
      rowExpansionColumn,
      rowReorderColumn,
      rowSelectionColumn,
    ]
  )

  const { tableState } = useTable<TData>({
    columns,
    data,
    expanded,
    getRowCanExpand,
    getRowId,
    getRowIsSelectable,
    isPaginationEnabled,
    isRowExpansionEnabled,
    isRowSelectionEnabled,
    isPaginatedOnClient,
    isSortedOnClient,
    onExpandedChange,
    onPaginationChange,
    onRowSelectionChange,
    onSortingChange,
    pagination,
    rowSelection,
    sorting,
  })
  const rows = tableState.getRowModel().rows
  const headersByColumnKeyRef = useRef(new Map<string, HTMLTableCellElement>())

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      onPaginationChange?.({
        pageIndex: page - 1,
        pageSize,
      })
    },
    [onPaginationChange]
  )
  const handleRowReordered = useCallback(
    // Adjust the indices to "debounce" cases where the user dragged an item
    // less than 50% of the way through another item.
    (sourceRow: Row<TData>, destinationRow: Row<TData>, closestEdge: Edge) => {
      const maxIndex = data?.length ? data?.length - 1 : 0
      const { sourceIndex, destinationIndex } = getReorderingIndices({
        sourceRow,
        destinationRow,
        closestEdge,
        maxIndex,
      })
      const input = {
        sourceIndex,
        destinationIndex,
        rawData: { sourceRow, destinationRow, closestEdge },
      }
      onRowReordered?.(input)
    },
    [onRowReordered, data?.length]
  )

  const tableDropTargetRef = useRef<HTMLDivElement>(null)
  const {
    isOpen: isDropTarget,
    open: prepareForDrop,
    close: stopPreparingForDrop,
  } = useDisclosure()

  useEffect(() => {
    const element = tableDropTargetRef.current
    if (!element || !onRowDroppedIntoTable) {
      return
    }
    return dropTargetForElements({
      canDrop: ({ source }) => {
        const dragData = source.data
        if (!isTableRowDragState<TData>(dragData)) {
          return false
        }
        return true
      },
      element,
      getData: (): TableDropTargetDragState => ({ isTableDropTarget: true, tableUuid: uuid }),
      getDropEffect: () => 'copy',
      onDragEnter: prepareForDrop,
      onDragLeave: stopPreparingForDrop,
      onDrop: stopPreparingForDrop,
    })
  }, [onRowDroppedIntoTable, prepareForDrop, stopPreparingForDrop, uuid])

  useEffect(() => {
    return monitorForElements({
      onDrop: async ({ source, location }) => {
        if (!onRowDroppedIntoTable) {
          return
        }
        const destination = location.current.dropTargets[0]
        if (!destination) {
          return
        }
        const sourceData = source.data
        const destinationData = destination.data
        if (
          !isTableRowDragState<TData>(sourceData) ||
          !isTableDropTargetDragState(destinationData) ||
          destinationData.tableUuid !== uuid
        ) {
          return
        }
        onRowDroppedIntoTable(sourceData.row)
      },
    })
  }, [onRowDroppedIntoTable, uuid])

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const tableContainerWidth = `${tableContainerRef.current?.clientWidth ?? 0}px`
  const showTableControls = !!(actions || isSearchingEnabled || showCount || title)

  const { isScrolledToLeft, isScrolledToRight } = useStickyScrollDetection(tableContainerRef)
  const hasRowLevelActions =
    isRowSelectionEnabled &&
    (!!rowLevelActions?.primaryActions?.length || !!rowLevelActions?.secondaryActions?.length)
  const handleClearSelections = useCallback(() => {
    onRowSelectionChange?.({})
  }, [onRowSelectionChange])

  // Merge external and internal refs so both get the input element reference
  const mergedSearchInputRef = useMergeRefs(externalSearchInputRef ?? null, searchInputRef)

  // Handle Cmd+F / Ctrl+F to focus the search input
  // First press focuses the input, second press (when already focused) falls through to browser find
  useEffect(() => {
    // Shortcut works if: enabled AND (internal search is enabled OR external ref is provided)
    if (!enableSearchShortcut || (!isSearchingEnabled && !externalSearchInputRef)) {
      return
    }

    // Use the external ref if provided and internal search is disabled, otherwise use internal ref
    const activeRef =
      !isSearchingEnabled && externalSearchInputRef ? externalSearchInputRef : searchInputRef

    const listener = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === KeyboardKey.F) {
        const isSearchInputFocused = document.activeElement === activeRef.current
        if (!isSearchInputFocused) {
          e.preventDefault()
          activeRef.current?.focus()
        }
        // If already focused, let the browser handle it (default find behavior)
      }
    }

    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [enableSearchShortcut, isSearchingEnabled, externalSearchInputRef])

  const headerGroups = tableState.getHeaderGroups()

  return (
    <TableContext.Provider value={{ size, uuid }}>
      <section className="isolate flex w-full flex-col gap-2">
        {showTableControls && (
          <Header
            actions={actions}
            {...(isSearchingEnabled
              ? {
                  onSearchQueryChange,
                  searchInputPlaceholder,
                  searchInputRef: mergedSearchInputRef,
                  searchQuery,
                }
              : {})}
            subtitle={subtitle}
            title={title}
            {...(showCount ? { count: totalCount } : {})}
          />
        )}
        <Flex vertical gap="12px">
          <VStack
            align="stretch"
            ref={tableContainerRef}
            className={twMerge(
              'max-w-full overflow-x-auto border border-neutral-100',
              hasBorderRadius ? 'rounded-md' : ''
            )}
          >
            <Flex
              vertical
              ref={tableDropTargetRef}
              style={{ width: 'fit-content', minWidth: '100%' }}
            >
              <StyledTable
                $isLoading={!!isLoading}
                $layout={layout}
                $minWidth={minWidth}
                ref={null}
                {...(shouldHideHeader ? { role: 'presentation' } : {})}
              >
                {!shouldHideHeader && (
                  <TableHeader
                    headersByColumnKeyRef={headersByColumnKeyRef}
                    isLoading={!!isLoading}
                    isScrolledToLeft={isScrolledToLeft}
                    isScrolledToRight={isScrolledToRight}
                    isSortingEnabled={isSortingEnabled}
                    headerGroups={headerGroups}
                  />
                )}
                {rows.map((row, index, arr) => (
                  <TableRow
                    key={row.id}
                    expanded={expanded}
                    getRowExpandedContent={getRowExpandedContent}
                    headersByColumnKeyRef={headersByColumnKeyRef}
                    isLastRow={index === arr.length - 1}
                    isRowHighlighted={getRowIsHighlighted(row)}
                    isRowReorderable={getRowIsReorderable(row)}
                    isRowSelectionEnabled={isRowSelectionEnabled}
                    isScrolledToLeft={isScrolledToLeft}
                    isScrolledToRight={isScrolledToRight}
                    onHoveredRowChange={onHoveredRowChange}
                    onRowReordered={handleRowReordered}
                    overrideReorderingWithinSameTableInvariant={
                      overrideReorderingWithinSameTableInvariant
                    }
                    row={row}
                    tableContainerWidth={tableContainerWidth}
                    verticalAlign={verticalAlign}
                  />
                ))}
              </StyledTable>
              {!rows.length && (
                <StyledEmptyStateWrapper $isDropTarget={isDropTarget}>
                  <EmptyState hideBorder size={size}>
                    {shouldHideEmptyStateImage ? null : emptyStateIcon ? (
                      <EmptyState.Image icon={emptyStateIcon} />
                    ) : (
                      emptyStateImage
                    )}
                    <EmptyState.Message>{emptyStateMessage}</EmptyState.Message>
                    {emptyStateAction}
                  </EmptyState>
                </StyledEmptyStateWrapper>
              )}
            </Flex>
          </VStack>
          <Flex justify="flex-end">
            {isPaginationEnabled && (
              <Pagination
                current={pagination.pageIndex + 1}
                onChange={handlePaginationChange}
                pageSize={pagination.pageSize}
                style={{ position: 'sticky', bottom: 0 }}
                total={totalCount}
                showSizeChanger={false}
              />
            )}
          </Flex>
        </Flex>
      </section>
      {hasRowLevelActions && (
        <TableActionToolbar
          onClearSelections={handleClearSelections}
          selectedRowCount={tableState.getSelectedRowModel().rows.length}
          rowLevelActions={rowLevelActions}
        />
      )}
    </TableContext.Provider>
  )
}

export default TableContent
